import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from '../../../shared/common.service';
import { LoaderService } from '../../../shared/loader.service';
import { ToastService } from '../../../shared/toast.service';

@Component({
  selector: 'app-community-session-success',
  templateUrl: './community-session-success.component.html',
  styleUrls: ['./community-session-success.component.scss']
})
export class CommunitySessionSuccessComponent implements OnInit, OnDestroy {
  
  sessionId: string | null = null;
  registrationData: any = null;
  error: string | null = null;
  countdown: number = 10;
  private countdownInterval: any;
  
  // For UI states
  verificationStatus: 'loading' | 'success' | 'error' = 'loading';
  
  // Animation states
  showConfetti: boolean = false;
  
  // Session details for display
  sessionDetails = {
    title: '',
    quote: '',
    charity: '',
    causeLabel: '',
    amount: 0,
    date: '',
    time: '',
    zoomLink: '',
    registrationId: ''
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private commonService: CommonService,
    private loader: LoaderService,
    private toast: ToastService
  ) {}


  showNoPaymentAlert: boolean = false;
redirectCountdown: number = 7;
timerProgress: number = 100;
private noPaymentInterval: any;


  ngOnInit(): void {
  this.route.queryParams.subscribe(params => {
    this.sessionId = params['session_id'] || null;

    if (!this.sessionId) {
      // ❌ No session ID — show non-declinable modal then redirect
      this.showNoPaymentAlert = true;
      this.startNoPaymentCountdown();
      return;
    }

    // ✅ Has session ID — existing flow untouched
    this.verifyPayment();
  });
}



startNoPaymentCountdown(): void {
  const totalSeconds = 5;
  this.redirectCountdown = totalSeconds;
  this.timerProgress = 100;

  this.noPaymentInterval = setInterval(() => {
    this.redirectCountdown--;
    this.timerProgress = (this.redirectCountdown / totalSeconds) * 100;

    if (this.redirectCountdown <= 0) {
      clearInterval(this.noPaymentInterval);
      this.redirectNow();
    }
  }, 1000);
}

redirectNow(): void {
  clearInterval(this.noPaymentInterval);
  this.showNoPaymentAlert = false;
  this.router.navigate(['/community']);
}


  ngOnDestroy(): void {
  if (this.countdownInterval) {
    clearInterval(this.countdownInterval);
  }
  if (this.noPaymentInterval) {
    clearInterval(this.noPaymentInterval);
  }
}

  verifyPayment(): void {
    this.verificationStatus = 'loading';
    this.loader.show();

    this.commonService.verifyCommunitySessionCheckout(this.sessionId!).subscribe({
      next: (response: any) => {
        this.verificationStatus = 'success';
        this.registrationData = response.data;
        this.showConfetti = true;
        
        // Populate session details for display
        if (response.data) {
          this.sessionDetails = {
            title: response.data.title || 'Community Session',
            quote: response.data.quote || '',
            charity: response.data.charity || '',
            causeLabel: response.data.causeLabel || '',
            amount: response.data.amount || 0,
            date: response.data.sessionDate || '',
            time: response.data.sessionTime || '',
            zoomLink: response.data.zoomLink || '',
            registrationId: response.data.registrationId || ''
          };
        }
        
        this.loader.hide();
        
        // Show success toast
        this.toast.showSuccess('Registration successful! Check your email for details.');
        
        // Start countdown for auto-redirect
        this.startCountdown();
      },
      error: (err: any) => {
        this.verificationStatus = 'error';
        this.loader.hide();
        this.toast.showError(err.error?.message || 'Failed to verify payment. Please contact support.');
      }
    });
  }

  startCountdown(): void {
    this.countdown = 10;
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(this.countdownInterval);
        this.goToMySessions();
      }
    }, 1000);
  }

  goToMySessions(): void {
    this.router.navigate(['/community']);
  }

  goToCommunitySessions(): void {
    this.router.navigate(['/community']);
  }

  copyZoomLink(): void {
    if (this.sessionDetails.zoomLink) {
      navigator.clipboard.writeText(this.sessionDetails.zoomLink).then(() => {
        this.toast.showSuccess('Zoom link copied to clipboard!');
      }).catch(() => {
        this.toast.showError('Failed to copy link');
      });
    }
  }

  addToCalendar(): void {
    if (!this.sessionDetails.date || !this.sessionDetails.time) return;
    
    // Create calendar event URL (Google Calendar format)
    const startDate = this.formatDateForCalendar(this.sessionDetails.date, this.sessionDetails.time);
    const endDate = this.addHours(startDate, 2); // Assume 2 hours duration
    
    const title = encodeURIComponent(this.sessionDetails.title);
    const details = encodeURIComponent(`Join Zoom: ${this.sessionDetails.zoomLink}`);
    const location = encodeURIComponent('Zoom');
    
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${details}&location=${location}`;
    
    window.open(googleCalendarUrl, '_blank');
  }

  private formatDateForCalendar(date: string, time: string): string {
    // Convert "YYYY-MM-DD" and "h:mm A" to Google Calendar format (YYYYMMDDTHHMMSS)
    const timeMatch = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!timeMatch) return '';
    
    let hours = parseInt(timeMatch[1]);
    const minutes = timeMatch[2];
    const ampm = timeMatch[3].toUpperCase();
    
    if (ampm === 'PM' && hours < 12) hours += 12;
    if (ampm === 'AM' && hours === 12) hours = 0;
    
    const formattedDate = date.replace(/-/g, '');
    const formattedTime = `${hours.toString().padStart(2, '0')}${minutes}00`;
    
    return `${formattedDate}T${formattedTime}`;
  }

  private addHours(dateStr: string, hours: number): string {
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(4, 6)) - 1;
    const day = parseInt(dateStr.substring(6, 8));
    const hour = parseInt(dateStr.substring(9, 11));
    const minute = parseInt(dateStr.substring(11, 13));
    const second = parseInt(dateStr.substring(13, 15));
    
    const date = new Date(year, month, day, hour, minute, second);
    date.setHours(date.getHours() + hours);
    
    return date.toISOString().replace(/[-:]/g, '').split('.')[0];
  }
}